import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, MapPin, Phone, Clock, CheckCircle, XCircle, Edit2, Package, AlertCircle, Loader } from 'lucide-react';
import TopHeader from './components/TopHeader';
import { bookingApi as apiService } from '../services/BookingService';
import { useSearchParams } from 'react-router-dom';

export default function BookingManagement() {
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState('auth'); // auth, list, details, editAddress, editDateTime
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [email, setEmail] = useState('');
  const [bookingHash, setBookingHash] = useState('');
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [unavailableDates, setUnavailableDates] = useState(new Set());

  const [editedAddress, setEditedAddress] = useState({
    address: '',
    apartment: '',
    city: ''
  });

  const [editedDateTime, setEditedDateTime] = useState({
    date: '',
    time: ''
  });

  const [selectedDate, setSelectedDate] = useState(null);

  // Load providers and services on mount, then handle deep-link token
  useEffect(() => {
    const init = async () => {
      const { providersData, servicesData } = await loadProvidersAndServices();
      const token = searchParams.get('token');
      if (token) {
        try {
          const decoded = JSON.parse(atob(token));
          if (decoded.email && decoded.hash) {
            setEmail(decoded.email);
            setBookingHash(decoded.hash);
            performLogin(decoded.email, decoded.hash, servicesData, providersData);
          }
        } catch {
          // malformed token — just show the auth form normally
        }
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load available time slots when date is selected for editing
  useEffect(() => {
    if (selectedDate && selectedBooking && currentView === 'editDateTime') {
      loadAvailableTimeSlots();
    }
  }, [selectedDate, selectedBooking, currentView]);

  // Check calendar availability when editing date/time
  useEffect(() => {
    if (selectedBooking && currentView === 'editDateTime') {
      checkCalendarAvailability();
    }
  }, [currentMonth, selectedBooking, currentView]);

  const loadProvidersAndServices = async () => {
    try {
      const [providersData, servicesData] = await Promise.all([
        apiService.getProviders(),
        apiService.getServices(),
      ]);
      setProviders(providersData);
      setServices(servicesData);
      return { providersData, servicesData };
    } catch (err) {
      console.error('Error loading providers and services:', err);
      return { providersData: [], servicesData: [] };
    }
  };

  const performLogin = async (loginEmail, loginHash, servicesData = services, providersData = providers) => {
    setIsLoading(true);
    setError(null);

    try {
      // Search for appointments with the provided hash
      const appointments = await apiService.searchAppointmentsByHash(loginHash);

      if (!appointments || appointments.length === 0) {
        setError('No booking found with this reference number');
        setIsLoading(false);
        return;
      }

      // Get the appointment
      const appointment = appointments[0];

      // Fetch the customer details
      const customerData = await apiService.getCustomer(appointment.customerId);

      // Verify email matches
      if (customerData.email.toLowerCase() !== loginEmail.toLowerCase()) {
        setError('Email does not match the booking reference');
        setIsLoading(false);
        return;
      }

      // Fetch all appointments for this customer
      const customerAppointments = await apiService.getCustomerAppointments(customerData.id);

      // Transform appointments to match the UI format
      const transformedBookings = await Promise.all(
        customerAppointments.map(async (apt) => {
          const service = servicesData.find(s => s.id === apt.serviceId) || { name: 'Unknown Service' };
          const provider = providersData.find(p => p.id === apt.providerId) || { firstName: 'Unknown', lastName: 'Provider' };

          return {
            id: apt.id,
            hash: apt.hash,
            createdDate: apt.book ? new Date(apt.book).toISOString().split('T')[0] : 'N/A',
            scheduledDate: apt.start ? new Date(apt.start).toISOString().split('T')[0] : 'N/A',
            scheduledTime: formatTimeRange(apt.start, apt.end),
            type: service.name,
            status: getAppointmentStatus(apt),
            address: customerData.address || '',
            apartment: '', // Can be extracted from address or notes if needed
            city: customerData.city || '',
            phone: customerData.phone || '',
            location: apt.location || '',
            notes: apt.notes || '',
            providerId: apt.providerId,
            serviceId: apt.serviceId,
            customerId: apt.customerId,
            googleCalendarId: apt.googleCalendarId,
            rawAppointment: apt,
            provider: `${provider.firstName} ${provider.lastName}`,
            changes: [] // Would need to track this separately
          };
        })
      );

      setCustomer(customerData);
      setBookings(transformedBookings);
      setIsAuthenticated(true);
      setCurrentView('list');
    } catch (err) {
      setError('Failed to authenticate. Please check your credentials.');
      console.error('Authentication error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = () => {
    if (!email || !bookingHash) {
      setError('Please enter both email and booking reference');
      return;
    }
    performLogin(email, bookingHash);
  };

  const getAppointmentStatus = (appointment) => {
    const now = new Date();
    const startTime = new Date(appointment.start);
    const endTime = new Date(appointment.end);

    if (endTime < now) {
      return 'Completed';
    } else if (startTime > now) {
      return 'Pending';
    } else {
      return 'In Progress';
    }
  };

  const formatTimeRange = (start, end) => {
    if (!start || !end) return 'N/A';

    const startDate = new Date(start);
    const endDate = new Date(end);

    const formatTime = (date) => {
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const minutesStr = minutes.toString().padStart(2, '0');
      return `${hours}:${minutesStr} ${ampm}`;
    };

    return `${formatTime(startDate)} - ${formatTime(endDate)}`;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${mins} minutes`;
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentView('auth');
    setEmail('');
    setBookingHash('');
    setBookings([]);
    setCustomer(null);
  };

  const filteredBookings = bookings.filter(booking => {
    if (statusFilter === 'all') return true;
    return booking.status.toLowerCase() === statusFilter.toLowerCase();
  });

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setCurrentView('details');
  };

  const handleEditAddress = () => {
    setEditedAddress({
      address: selectedBooking.address,
      apartment: selectedBooking.apartment,
      city: selectedBooking.city
    });
    setCurrentView('editAddress');
  };

  const handleEditDateTime = () => {
    setEditedDateTime({
      date: selectedBooking.scheduledDate,
      time: selectedBooking.scheduledTime
    });
    setCurrentMonth(new Date(selectedBooking.scheduledDate));
    setCurrentView('editDateTime');
  };

  const saveAddress = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Update customer address in Easy!Appointments
      const updatedCustomer = {
        ...customer,
        address: editedAddress.address,
        city: editedAddress.city,
      };

      await apiService.updateCustomer(customer.id, updatedCustomer);

      // Update appointment location if needed
      const updatedAppointment = {
        ...selectedBooking.rawAppointment,
        location: `${editedAddress.address}${editedAddress.apartment ? ', ' + editedAddress.apartment : ''}, ${editedAddress.city}`
      };

      await apiService.updateAppointment(selectedBooking.id, updatedAppointment);

      // Update local state
      const updatedBookings = bookings.map(b => {
        if (b.id === selectedBooking.id) {
          return {
            ...b,
            address: editedAddress.address,
            apartment: editedAddress.apartment,
            city: editedAddress.city,
            location: updatedAppointment.location,
            changes: [
              ...b.changes,
              { 
                date: new Date().toISOString().split('T')[0], 
                description: `Address updated to ${editedAddress.address}${editedAddress.apartment ? ', ' + editedAddress.apartment : ''}, ${editedAddress.city}` 
              }
            ]
          };
        }
        return b;
      });

      setBookings(updatedBookings);
      setSelectedBooking(updatedBookings.find(b => b.id === selectedBooking.id));
      setCustomer(updatedCustomer);
      setCurrentView('details');
    } catch (err) {
      setError('Failed to update address. Please try again.');
      console.error('Error updating address:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveDateTime = async () => {
    if (!editedDateTime.date || !editedDateTime.time) {
      setError('Please select both date and time');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Parse the time and create new start/end times
      const [startTime, endTime] = editedDateTime.time.split(' - ');
      const service = services.find(s => s.id === selectedBooking.serviceId);
      const duration = service ? service.duration : 60; // default to 60 minutes

      const newStart = apiService.formatDateTime(new Date(editedDateTime.date), startTime);
      const newEnd = apiService.calculateEndTime(new Date(editedDateTime.date), startTime, duration);

      // Update appointment in Easy!Appointments
      const updatedAppointment = {
        ...selectedBooking.rawAppointment,
        start: newStart,
        end: newEnd
      };

      await apiService.updateAppointment(selectedBooking.id, updatedAppointment);

      // Update local state
      const updatedBookings = bookings.map(b => {
        if (b.id === selectedBooking.id) {
          return {
            ...b,
            scheduledDate: editedDateTime.date,
            scheduledTime: editedDateTime.time,
            rawAppointment: updatedAppointment,
            changes: [
              ...b.changes,
              { 
                date: new Date().toISOString().split('T')[0], 
                description: `Date/Time updated to ${editedDateTime.date} at ${editedDateTime.time}` 
              }
            ]
          };
        }
        return b;
      });

      setBookings(updatedBookings);
      setSelectedBooking(updatedBookings.find(b => b.id === selectedBooking.id));
      setCurrentView('details');
    } catch (err) {
      setError('Failed to update date/time. Please try again.');
      console.error('Error updating date/time:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await apiService.deleteAppointment(selectedBooking.id);

      // Update local state
      const updatedBookings = bookings.map(b => {
        if (b.id === selectedBooking.id) {
          return {
            ...b,
            status: 'Cancelled',
            changes: [
              ...b.changes,
              { 
                date: new Date().toISOString().split('T')[0], 
                description: 'Booking cancelled by customer' 
              }
            ]
          };
        }
        return b;
      });

      setBookings(updatedBookings);
      setSelectedBooking(updatedBookings.find(b => b.id === selectedBooking.id));
      setCurrentView('list');
    } catch (err) {
      setError('Failed to cancel booking. Please try again.');
      console.error('Error cancelling booking:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableTimeSlots = async () => {
    if (!selectedBooking || !selectedDate) return;

    setIsLoading(true);
    setError(null);

    try {
      const provider = providers.find(p => p.id === selectedBooking.providerId);
      if (!provider) {
        setError('Provider not found');
        return;
      }

      const availabilities = await apiService.getAvailabilities(
        selectedBooking.providerId,
        selectedBooking.serviceId,
        new Date(selectedDate)
      );

      setAvailableTimeSlots(availabilities);
    } catch (err) {
      setError('Failed to load available time slots');
      console.error('Error loading time slots:', err);
      setAvailableTimeSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  const isWorkingDay = (date) => {
    if (!selectedBooking) return true;

    const provider = providers.find(p => p.id === selectedBooking.providerId);
    if (!provider || !provider.settings || !provider.settings.workingPlan) {
      return true;
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];
    const workingPlan = provider.settings.workingPlan;

    return workingPlan[dayName] !== null && workingPlan[dayName] !== undefined;
  };

  const hasWorkingPlanException = (date) => {
    if (!selectedBooking) return null;

    const provider = providers.find(p => p.id === selectedBooking.providerId);
    if (!provider || !provider.settings || !provider.settings.workingPlanExceptions) {
      return null;
    }

    const dateStr = apiService.formatDate(date);
    return provider.settings.workingPlanExceptions[dateStr] || null;
  };

  const checkCalendarAvailability = () => {
    if (!selectedBooking) return;

    const calendar = generateCalendarDays();
    const unavailable = new Set();

    calendar.forEach((day) => {
      if (day && day.date) {
        const date = new Date(day.date);
        const exception = hasWorkingPlanException(date);
        
        if (exception) {
          return;
        }
        
        if (!isWorkingDay(date) || day.isUnavailable) {
          unavailable.add(date.toDateString());
        }
      }
    });

    setUnavailableDates(unavailable);
  };

  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'pending': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'in progress': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch(status.toLowerCase()) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'in progress': return <Package className="w-4 h-4" />;
      default: return null;
    }
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];

    // Empty cells before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      days.push({
        day,
        date: dateStr,
        isUnavailable: date < today
      });
    }

    return days;
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const today = new Date();
    today.setDate(1);
    today.setHours(0, 0, 0, 0);
    
    if (newMonth >= today) {
      setCurrentMonth(newMonth);
    }
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const canGoPrevious = () => {
    const today = new Date();
    const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return currentMonth > thisMonth;
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Loading Overlay Component
  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
        <Loader className="w-12 h-12 text-purple-600 animate-spin" />
        <p className="text-gray-700 font-semibold">Processing...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <TopHeader />

      {isLoading && <LoadingOverlay />}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {!isAuthenticated ? (
          /* Login View */
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Your Booking</h1>
                <p className="text-gray-600">Access your booking details and make changes</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Booking Reference
                  </label>
                  <input
                    type="text"
                    value={bookingHash}
                    onChange={(e) => setBookingHash(e.target.value)}
                    placeholder="Enter your booking reference"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Your booking reference was sent to your email
                  </p>
                </div>

                <button
                  onClick={handleLogin}
                  disabled={isLoading || !email || !bookingHash}
                  className={`w-full py-4 rounded-full text-lg font-semibold transition-all transform ${
                    !isLoading && email && bookingHash
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-lg hover:scale-105'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? 'Authenticating...' : 'Access My Booking'}
                </button>
              </div>

              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-gray-600">
                  Need help? Contact us at{' '}
                  <a href="mailto:support@example.com" className="text-purple-600 hover:text-purple-800 font-semibold">
                    support@example.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {/* View 1: Booking List */}
            {currentView === 'list' && (
              <div>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">My Bookings</h1>
                    <p className="text-gray-600">Welcome back, {customer?.firstName}!</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-6 py-3 rounded-full bg-white text-gray-700 font-semibold shadow-md hover:shadow-lg transition-all"
                  >
                    Logout
                  </button>
                </div>

                {/* Filter Tabs */}
                <div className="bg-white rounded-2xl shadow-md p-2 mb-6 inline-flex gap-2">
                  {['all', 'pending', 'completed', 'cancelled'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setStatusFilter(filter)}
                      className={`px-6 py-2 rounded-full font-semibold transition-all capitalize ${
                        statusFilter === filter
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>

                {/* Bookings Grid */}
                {filteredBookings.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No bookings found</h3>
                    <p className="text-gray-500">
                      {statusFilter === 'all' 
                        ? "You don't have any bookings yet." 
                        : `No ${statusFilter} bookings found.`}
                    </p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {filteredBookings.map((booking) => (
                      <div
                        key={booking.id}
                        onClick={() => handleBookingClick(booking)}
                        className="bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold mb-2 ${getStatusColor(booking.status)}`}>
                              {getStatusIcon(booking.status)}
                              {booking.status}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900">{booking.type}</h3>
                            <p className="text-sm text-gray-500">Ref: {booking.hash}</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-3 text-gray-700">
                            <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" />
                            <span className="font-medium">{booking.scheduledDate}</span>
                          </div>
                          <div className="flex items-center gap-3 text-gray-700">
                            <Clock className="w-5 h-5 text-purple-600 flex-shrink-0" />
                            <span>{booking.scheduledTime}</span>
                          </div>
                          <div className="flex items-center gap-3 text-gray-700">
                            <MapPin className="w-5 h-5 text-purple-600 flex-shrink-0" />
                            <span className="line-clamp-1">{booking.address}, {booking.city}</span>
                          </div>
                          {booking.provider && (
                            <div className="flex items-center gap-3 text-gray-700">
                              <Package className="w-5 h-5 text-purple-600 flex-shrink-0" />
                              <span>Provider: {booking.provider}</span>
                            </div>
                          )}
                        </div>

                        <div className="mt-4 pt-4 border-t flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Booked on {booking.createdDate}
                          </span>
                          <span className="text-purple-600 font-semibold">View Details →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* View 2: Booking Details */}
            {currentView === 'details' && selectedBooking && (
              <div>
                <button
                  onClick={() => setCurrentView('list')}
                  className="flex items-center gap-2 text-purple-600 font-semibold mb-6 hover:text-purple-700 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Bookings
                </button>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6 pb-6 border-b">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedBooking.type}</h2>
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(selectedBooking.status)}`}>
                        {getStatusIcon(selectedBooking.status)}
                        {selectedBooking.status}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Booking Reference</div>
                      <div className="text-lg font-bold text-gray-900">{selectedBooking.hash}</div>
                      <div className="text-sm text-gray-500 mt-1">Booked on {selectedBooking.createdDate}</div>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="space-y-6">
                    {/* Date & Time */}
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-gray-500 mb-3">Scheduled Date & Time</h3>
                          <div className="flex items-center gap-3 mb-2">
                            <Calendar className="w-6 h-6 text-purple-600" />
                            <span className="text-xl font-bold text-gray-900">{selectedBooking.scheduledDate}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Clock className="w-6 h-6 text-purple-600" />
                            <span className="text-lg font-semibold text-gray-700">{selectedBooking.scheduledTime}</span>
                          </div>
                        </div>
                        {selectedBooking.status === 'Pending' && (
                          <button
                            onClick={handleEditDateTime}
                            className="px-4 py-2 rounded-lg bg-white text-purple-600 font-semibold hover:shadow-md transition-all flex items-center gap-2"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Location */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-500">Service Address</label>
                        {selectedBooking.status === 'Pending' && (
                          <button
                            onClick={handleEditAddress}
                            className="text-purple-600 font-semibold hover:text-purple-700 transition-colors flex items-center gap-1"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                      </div>
                      <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                        <MapPin className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-gray-900">{selectedBooking.address}</div>
                          {selectedBooking.apartment && (
                            <div className="text-gray-600">{selectedBooking.apartment}</div>
                          )}
                          <div className="text-gray-600">{selectedBooking.city}</div>
                        </div>
                      </div>
                    </div>

                    {/* Provider */}
                    {selectedBooking.provider && (
                      <div>
                        <label className="text-sm font-semibold text-gray-500">Service Provider</label>
                        <div className="flex items-center gap-2 mt-2">
                          <Package className="w-5 h-5 text-purple-600" />
                          <span className="font-semibold text-gray-900">{selectedBooking.provider}</span>
                        </div>
                      </div>
                    )}

                    {/* Phone */}
                    <div>
                      <label className="text-sm font-semibold text-gray-500">Phone Number</label>
                      <div className="flex items-center gap-2 mt-2">
                        <Phone className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-gray-900">{selectedBooking.phone}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedBooking.notes && (
                      <div>
                        <label className="text-sm font-semibold text-gray-500">Notes</label>
                        <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                          <p className="text-gray-700">{selectedBooking.notes}</p>
                        </div>
                      </div>
                    )}

                    {/* Cancel Button */}
                    {selectedBooking.status === 'Pending' && (
                      <div className="pt-6 border-t">
                        <button
                          onClick={handleCancelBooking}
                          className="w-full py-3 rounded-lg border-2 border-red-500 text-red-600 font-semibold hover:bg-red-50 transition-all"
                        >
                          Cancel This Booking
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Changes Log */}
                  {selectedBooking.changes.length > 0 && (
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Change History</h3>
                      <div className="space-y-3">
                        {selectedBooking.changes.map((change, index) => (
                          <div key={index} className="flex gap-3 text-sm">
                            <div className="text-gray-500 font-medium whitespace-nowrap">{change.date}</div>
                            <div className="text-gray-700">{change.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* View 3: Edit Address */}
            {currentView === 'editAddress' && selectedBooking && (
              <div>
                <button
                  onClick={() => setCurrentView('details')}
                  className="flex items-center gap-2 text-purple-600 font-semibold mb-6 hover:text-purple-700 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Details
                </button>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Edit Address</h2>

                  {/* Booking Summary */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6">
                    <div className="text-sm text-gray-600 mb-1">Booking Summary</div>
                    <div className="font-semibold text-gray-900">{selectedBooking.type}</div>
                    <div className="text-sm text-gray-600">{selectedBooking.scheduledDate} at {selectedBooking.scheduledTime}</div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address</label>
                      <input
                        type="text"
                        value={editedAddress.address}
                        onChange={(e) => setEditedAddress({...editedAddress, address: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>

                {/*
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Apartment/Unit Number (Optional)</label>
                      <input
                        type="text"
                        value={editedAddress.apartment}
                        onChange={(e) => setEditedAddress({...editedAddress, apartment: e.target.value})}
                        placeholder="Unit 5B"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>
                    */}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                      <select
                        type="text"
                        value={editedAddress.city}
                        onChange={(e) => setEditedAddress({...editedAddress, city: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                      >
                        <option value="ottawa" >Ottawa</option>
                        <option value="gatineau" >Gatineau</option>
                      </select>
                    </div>

                    <button
                      onClick={saveAddress}
                      disabled={!editedAddress.address || !editedAddress.city || isLoading}
                      className={`w-full py-4 rounded-full text-lg font-semibold transition-all transform ${
                        editedAddress.address && editedAddress.city && !isLoading
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-lg hover:scale-105'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* View 4: Edit Date/Time */}
            {currentView === 'editDateTime' && selectedBooking && (
              <div>
                <button
                  onClick={() => setCurrentView('details')}
                  className="flex items-center gap-2 text-purple-600 font-semibold mb-6 hover:text-purple-700 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Details
                </button>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Edit Date & Time</h2>

                  {/* Booking Summary */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6">
                    <div className="text-sm text-gray-600 mb-1">Booking Summary</div>
                    <div className="font-semibold text-gray-900">{selectedBooking.type}</div>
                    <div className="text-sm text-gray-600">{selectedBooking.address}, {selectedBooking.city}</div>
                  </div>

                  {/* Calendar */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Select a Date</h3>
                    <div className="bg-gray-50 rounded-xl p-4">
                      {/* Month Navigation */}
                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={goToPreviousMonth}
                          disabled={!canGoPrevious()}
                          className={`p-2 rounded-lg transition-all ${
                            canGoPrevious()
                              ? 'hover:bg-gray-200 text-gray-700'
                              : 'text-gray-300 cursor-not-allowed'
                          }`}
                        >
                          <ArrowLeft className="w-5 h-5" />
                        </button>
                        <span className="font-semibold text-gray-900">{formatMonthYear(currentMonth)}</span>
                        <button
                          onClick={goToNextMonth}
                          className="p-2 rounded-lg hover:bg-gray-200 text-gray-700 transition-all"
                        >
                          <Calendar className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="grid grid-cols-7 gap-2 mb-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="text-center text-xs font-semibold text-gray-600">{day}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-2">
                        {generateCalendarDays().map((day, index) => {
                          if (!day) {
                            return <div key={index} className="aspect-square" />;
                          }

                          const date = new Date(day.date);
                          const isUnavailable = day.isUnavailable || unavailableDates.has(date.toDateString());
                          const isSelected = selectedDate === day.date;

                          return (
                            <button
                              key={index}
                              disabled={isUnavailable}
                              onClick={() => {
                                if (!isUnavailable) {
                                  setSelectedDate(day.date);
                                  setEditedDateTime({...editedDateTime, date: day.date});
                                }
                              }}
                              className={`aspect-square rounded-lg text-sm font-semibold transition-all ${
                                isUnavailable 
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :
                                isSelected 
                                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' :
                                'bg-white hover:bg-purple-50 text-gray-900 hover:shadow-md'
                              }`}
                            >
                              {day.day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Time Slots */}
                  {selectedDate && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Select a Time</h3>
                      {availableTimeSlots.length === 0 && !isLoading ? (
                        <div className="text-center py-8 text-gray-500">
                          No available time slots for this date
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {availableTimeSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setEditedDateTime({...editedDateTime, time: slot})}
                              className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                                editedDateTime.time === slot
                                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                                  : 'bg-white border-2 border-gray-200 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={saveDateTime}
                    disabled={!editedDateTime.date || !editedDateTime.time || isLoading}
                    className={`w-full py-4 rounded-full text-lg font-semibold transition-all ${
                      editedDateTime.date && editedDateTime.time && !isLoading
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-lg transform hover:scale-105'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}